import type { LanguageCode } from '@/i18n/languages';

import type { ReleaseNotesEntry } from './manifest';

/** Served from assets/release-notes/ through the public/assets symlink. */
export const NOTES_BASE_PATH = '/assets/release-notes';

export function notesDir(version: string): string {
    return `${NOTES_BASE_PATH}/v${version}/`;
}

export function notesUrl(version: string, language: LanguageCode): string {
    return `${notesDir(version)}${language}.md`;
}

/** The UI language when this release has it, else English (always present). */
export function resolveNotesLanguage(
    entry: ReleaseNotesEntry,
    uiLanguage: string,
): { language: LanguageCode; fellBack: boolean } {
    const match = entry.languages.find((l) => l === uiLanguage);
    return match ? { language: match, fellBack: false } : { language: 'en', fellBack: true };
}

/** Resolves to the release's notes as HTML. */
export type NotesLoader = (entry: ReleaseNotesEntry, language: LanguageCode) => Promise<string>;

export interface LoadDeps {
    fetch: (url: string) => Promise<Response>;
    render: (markdown: string, baseDir: string) => Promise<string>;
}

const defaultDeps: LoadDeps = {
    fetch: (url) => fetch(url),
    render: async (markdown, baseDir) =>
        (await import('./render-markdown')).renderNotesMarkdown(markdown, baseDir),
};

/** Successes only — a failure must be retryable. */
const cache = new Map<string, string>();

export function clearNotesCache(): void {
    cache.clear();
}

/** Fetch + render one release's notes. Rejects on a non-ok response, on an
 *  HTML response (the SPA fallback answers unknown paths with index.html and
 *  a 200), and when the renderer chunk fails to load. */
export async function loadNotesHtml(
    entry: ReleaseNotesEntry,
    language: LanguageCode,
    deps: LoadDeps = defaultDeps,
): Promise<string> {
    const url = notesUrl(entry.version, language);
    const hit = cache.get(url);
    if (hit !== undefined) return hit;
    const res = await deps.fetch(url);
    const type = res.headers.get('content-type') ?? '';
    if (!res.ok || type.includes('text/html')) {
        throw new Error(`release notes unavailable: ${url} (${res.status}${type ? `, ${type}` : ''})`);
    }
    const html = await deps.render(await res.text(), notesDir(entry.version));
    cache.set(url, html);
    return html;
}
